import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  type PropsWithChildren,
} from "react";

export type DesignTokens = typeof defaultDesignTokens;

export type DeepPartial<T> = {
  [K in keyof T]?: T[K] extends object ? DeepPartial<T[K]> : T[K];
};

const DesignSystemContext = createContext<DesignTokens | null>(null);

const FONT_VARIABLE_KEYS = {
  sans: "--app-font-sans",
  heading: "--app-font-heading",
  mono: "--app-font-mono",
} as const;

export const defaultDesignTokens = {
  typography: {
    fonts: {
      sans: '"Inter", "IBM Plex Sans", "SF Pro Text", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      heading:
        '"Inter", "IBM Plex Sans", "SF Pro Display", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      mono: '"JetBrains Mono", "Fira Code", SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
    },
    // Glass effect typography styles
    pageTitle: "font-heading text-2xl sm:text-3xl lg:text-4xl font-semibold text-white",
    sectionTitle: "text-lg font-semibold text-white sm:text-xl",
    cardTitle: "text-lg font-semibold text-white",
    subtitle: "text-sm font-medium text-slate-300/80",
    body: "text-sm text-white/80 leading-relaxed",
    bodyMuted: "text-sm text-white/60",
    caption: "text-xs text-white/60",
    label: "text-xs font-semibold uppercase tracking-wide text-white/70",
    description: "text-sm text-slate-300/80 leading-relaxed",
    subtle: "font-sans text-xs uppercase tracking-wide text-white/60",
  },
  surfaces: {
    // Base app backgrounds
    app: "bg-slate-950 text-slate-100",
    canvas: "bg-slate-950/95 text-slate-100",

    // Glass effect surfaces
    header: "bg-slate-950/90 border-b border-white/10 text-gray-100 shadow-sm backdrop-blur",

    // Cards with glass effect
    card: "rounded-2xl border border-white/10 bg-neutral-900/60 backdrop-blur-md",
    cardStrong: "rounded-2xl border border-slate-800/70 bg-slate-900/70 shadow-[0_30px_80px_-60px_rgba(15,23,42,0.9)]",
    cardHover: "transition-transform duration-300 hover:-translate-y-[2px]",

    // Panel variations
    panel: "rounded-xl border border-white/10 bg-white/5 text-gray-100",
    panelStrong: "rounded-xl border border-white/15 bg-slate-900/80 text-gray-100 shadow-elevated",
    panelMuted: "rounded-lg border border-white/5 bg-white/5 text-gray-200",

    // List items with glass effect
    listItem: "rounded-lg border border-white/10 bg-white/5 px-3 py-2.5 text-sm text-white/80 transition hover:bg-white/10",

    // Empty states
    emptyState: "flex flex-col items-center justify-center gap-3 rounded-2xl border border-dashed border-white/15 bg-white/5 px-3 py-10 text-center",

    // Table with glass effect
    table: "rounded-xl border border-white/10 bg-white/5 text-gray-100 shadow-inner",
    tableRow: "border-b border-white/5 transition hover:bg-white/5",

    // Alert surfaces with glass effect
    warning: "bg-warning-500/10 border border-warning-500/40 text-warning-100",
    danger: "bg-error-500/10 border border-error-500/40 text-error-100",
    success: "bg-success-500/10 border border-success-500/40 text-success-100",
    info: "bg-info-500/10 border border-info-500/40 text-info-100",

    // Modals with glass effect
    modalBackdrop: "fixed inset-0 z-[9999] bg-black/85 backdrop-blur-md",
    modalContainer: "rounded-2xl border border-white/10 bg-slate-900/95 shadow-overlay backdrop-blur-lg",
    modalHeader: "border-b border-white/10 px-5 py-4",
    modalBody: "px-5 py-5",
    modalFooter: "border-t border-white/10 bg-slate-900/40 px-5 py-4",

    // Form inputs with glass effect
    input: "rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm text-white placeholder-white/40 transition focus:border-primary-500/50 focus:bg-white/10 focus:outline-none",
    inputDisabled: "cursor-not-allowed opacity-50",
    select: "rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm text-white transition focus:border-primary-500/50 focus:bg-white/10 focus:outline-none",
    textarea: "rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm text-white placeholder-white/40 transition focus:border-primary-500/50 focus:bg-white/10 focus:outline-none",
  },
  layout: {
    appShell: "min-h-screen w-full font-sans bg-slate-950",
    fullHeight: "min-h-screen flex flex-col",
    pageGutter: "px-6 md:px-8 lg:px-12",
    pageSection: "px-6 md:px-8 lg:px-12 py-6 space-y-6",
    mainContent: "flex-1 pb-12",
    section: "rounded-2xl p-6 md:p-8 space-y-5",
    sectionDense: "rounded-xl p-4 space-y-3",
    inset: "rounded-lg p-4 space-y-3",
    tabsContainer: "px-6 md:px-8 lg:px-12 pt-5 pb-3",
    tabContent: "px-6 md:px-8 lg:px-12 pt-6 pb-10 space-y-6",
    stack: "space-y-3",
    stackDense: "space-y-2",
    stackRelaxed: "space-y-6",
    stackLoose: "space-y-8",
    grid: "grid gap-6",
    gridTwoCol: "grid grid-cols-1 gap-6 lg:grid-cols-2",
    gridThreeCol: "grid grid-cols-1 gap-6 lg:grid-cols-2 xl:grid-cols-3",
  },
  components: {
    // Tab components with glass effect
    tabBar: "flex gap-2 overflow-x-auto",
    tabButton: {
      base: "px-2 py-2.5 text-sm font-semibold rounded-xl border transition-all duration-200 flex items-center gap-2",
      active: "bg-primary-500/15 border-primary-500/50 text-primary-200 shadow-inner",
      inactive: "bg-transparent border-transparent text-white/60 hover:text-white/80 hover:bg-white/5",
    },
    tabs: {
      container: "border-b border-white/10",
      nav: "-mb-px flex space-x-2",
      button: "whitespace-nowrap border-b-2 py-2 px-1 font-medium text-sm transition",
      buttonActive: "border-primary-500 text-primary-500",
      buttonInactive: "border-transparent text-white/60 hover:text-white/80",
      badge: "ml-2 rounded-full px-2 py-0.5 text-xs",
      badgeActive: "bg-primary-500/20 text-primary-500",
      badgeInactive: "bg-white/10 text-white/60",
    },

    // Buttons with glass effect
    // Size variants: xs (extra small), sm (small), md (medium - default), lg (large)
    primaryButton: "inline-flex items-center justify-center gap-1.5 rounded-lg border border-primary-500/30 bg-primary-500/20 px-4 py-2 text-sm font-semibold text-primary-100 transition hover:bg-primary-500/30 disabled:cursor-not-allowed disabled:opacity-50",
    primaryButtonXs: "inline-flex items-center gap-1.5 rounded-lg border border-primary-500/30 bg-primary-500/20 px-2 py-1 text-xs font-semibold text-primary-100 transition hover:bg-primary-500/30 disabled:cursor-not-allowed disabled:opacity-50",
    primaryButtonSmall: "inline-flex items-center gap-1.5 rounded-lg border border-primary-500/30 bg-primary-500/20 px-3 py-1.5 text-xs font-semibold text-primary-100 transition hover:bg-primary-500/30 disabled:cursor-not-allowed disabled:opacity-50",
    primaryButtonLarge: "inline-flex items-center justify-center gap-2 rounded-lg border border-primary-500/30 bg-primary-500/20 px-6 py-3 text-base font-semibold text-primary-100 transition hover:bg-primary-500/30 disabled:cursor-not-allowed disabled:opacity-50",

    secondaryButton: "inline-flex items-center justify-center gap-1.5 rounded-lg border border-white/10 bg-white/5 px-4 py-2 text-sm font-semibold text-white/80 transition hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-50",
    secondaryButtonXs: "inline-flex items-center gap-1.5 rounded-lg border border-white/10 bg-white/5 px-2 py-1 text-xs font-semibold text-white/80 transition hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-50",
    secondaryButtonSmall: "inline-flex items-center gap-1.5 rounded-lg border border-white/10 bg-white/5 px-3 py-1.5 text-xs font-semibold text-white/80 transition hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-50",
    secondaryButtonLarge: "inline-flex items-center justify-center gap-2 rounded-lg border border-white/10 bg-white/5 px-6 py-3 text-base font-semibold text-white/80 transition hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-50",

    successButton: "inline-flex items-center gap-1.5 rounded-lg border border-emerald-500/30 bg-emerald-500/20 px-4 py-2 text-sm font-semibold text-emerald-200 transition hover:bg-emerald-500/30 disabled:cursor-not-allowed disabled:opacity-50",
    successButtonXs: "inline-flex items-center gap-1.5 rounded-lg border border-emerald-500/30 bg-emerald-500/20 px-2 py-1 text-xs font-semibold text-emerald-200 transition hover:bg-emerald-500/30 disabled:cursor-not-allowed disabled:opacity-50",
    successButtonSmall: "inline-flex items-center gap-1.5 rounded-lg border border-white/10 bg-emerald-500/20 px-3 py-1.5 text-xs font-semibold text-emerald-200 transition hover:bg-emerald-500/30 disabled:cursor-not-allowed disabled:opacity-50",
    successButtonLarge: "inline-flex items-center gap-2 rounded-lg border border-emerald-500/30 bg-emerald-500/20 px-6 py-3 text-base font-semibold text-emerald-200 transition hover:bg-emerald-500/30 disabled:cursor-not-allowed disabled:opacity-50",

    dangerButton: "inline-flex items-center gap-1.5 rounded-lg border border-rose-500/30 bg-rose-500/20 px-4 py-2 text-sm font-semibold text-rose-200 transition hover:bg-rose-500/30 disabled:cursor-not-allowed disabled:opacity-50",
    dangerButtonXs: "inline-flex items-center gap-1.5 rounded-lg border border-rose-500/30 bg-rose-500/20 px-2 py-1 text-xs font-semibold text-rose-200 transition hover:bg-rose-500/30 disabled:cursor-not-allowed disabled:opacity-50",
    dangerButtonSmall: "inline-flex items-center gap-1.5 rounded-lg border border-rose-500/30 bg-rose-500/20 px-3 py-1.5 text-xs font-semibold text-rose-200 transition hover:bg-rose-500/30 disabled:cursor-not-allowed disabled:opacity-50",
    dangerButtonLarge: "inline-flex items-center gap-2 rounded-lg border border-rose-500/30 bg-rose-500/20 px-6 py-3 text-base font-semibold text-rose-200 transition hover:bg-rose-500/30 disabled:cursor-not-allowed disabled:opacity-50",

    warningButton: "inline-flex items-center gap-1.5 rounded-lg border border-amber-500/30 bg-amber-500/20 px-4 py-2 text-sm font-semibold text-amber-200 transition hover:bg-amber-500/30 disabled:cursor-not-allowed disabled:opacity-50",
    warningButtonXs: "inline-flex items-center gap-1.5 rounded-lg border border-amber-500/30 bg-amber-500/20 px-2 py-1 text-xs font-semibold text-amber-200 transition hover:bg-amber-500/30 disabled:cursor-not-allowed disabled:opacity-50",
    warningButtonSmall: "inline-flex items-center gap-1.5 rounded-lg border border-amber-500/30 bg-amber-500/20 px-3 py-1.5 text-xs font-semibold text-amber-200 transition hover:bg-amber-500/30 disabled:cursor-not-allowed disabled:opacity-50",
    warningButtonLarge: "inline-flex items-center gap-2 rounded-lg border border-amber-500/30 bg-amber-500/20 px-6 py-3 text-base font-semibold text-amber-200 transition hover:bg-amber-500/30 disabled:cursor-not-allowed disabled:opacity-50",

    ghostButton: "inline-flex items-center gap-1.5 rounded-lg px-4 py-2 text-sm font-semibold text-white/80 transition hover:bg-white/5 disabled:cursor-not-allowed disabled:opacity-50",
    ghostButtonXs: "inline-flex items-center gap-1.5 rounded-lg px-2 py-1 text-xs font-semibold text-white/80 transition hover:bg-white/5 disabled:cursor-not-allowed disabled:opacity-50",
    ghostButtonSmall: "inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold text-white/80 transition hover:bg-white/5 disabled:cursor-not-allowed disabled:opacity-50",
    ghostButtonLarge: "inline-flex items-center gap-2 rounded-lg px-6 py-3 text-base font-semibold text-white/80 transition hover:bg-white/5 disabled:cursor-not-allowed disabled:opacity-50",

    // Icon button variants (square buttons for icons only)
    iconButton: "inline-flex items-center justify-center rounded-lg border border-white/10 bg-white/5 p-2 text-white/80 transition hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-50",
    iconButtonXs: "inline-flex items-center justify-center rounded-lg border border-white/10 bg-white/5 p-1 text-white/80 transition hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-50",
    iconButtonSmall: "inline-flex items-center justify-center rounded-lg border border-white/10 bg-white/5 p-1.5 text-white/80 transition hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-50",
    iconButtonLarge: "inline-flex items-center justify-center rounded-lg border border-white/10 bg-white/5 p-3 text-white/80 transition hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-50",

    // Legacy support
    neutralButton: "inline-flex items-center justify-center gap-1.5 rounded-lg border border-white/10 bg-white/5 px-4 py-2 text-sm font-semibold text-white/80 transition hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-50",

    // Badges with glass effect
    badge: {
      base: "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold",
      primary: "bg-primary-500/20 text-primary-500 border border-primary-500/30",
      secondary: "bg-white/10 text-white/80 border border-white/10",
      success: "bg-emerald-500/20 text-emerald-300 border border-emerald-500/30",
      danger: "bg-rose-500/20 text-rose-300 border border-rose-500/30",
      warning: "bg-amber-500/20 text-amber-300 border border-amber-500/30",
      info: "bg-sky-500/20 text-sky-300 border border-sky-500/30",
      neutral: "bg-white/10 text-white/60",
    },
    badgeMuted: "rounded-full bg-white/10 px-2 py-0.5 text-[11px] font-semibold text-white/60",
    tabBadge: "inline-flex items-center justify-center rounded-full px-2 py-0.5 text-xs font-semibold bg-error-500/20 text-error-200 border border-error-500/30",

    // Icons
    icon: {
      base: "shrink-0",
      small: "h-4 w-4",
      medium: "h-5 w-5",
      large: "h-6 w-6",
      muted: "text-white/30",
      normal: "text-white/60",
      primary: "text-primary-500",
    },

    // Dropdowns with glass effect
    dropdown: {
      container: "absolute z-[10000] mt-2 rounded-lg border border-white/10 bg-slate-900/95 shadow-overlay backdrop-blur-lg",
      item: "flex items-center gap-2 px-3 py-2 text-sm text-white/80 transition hover:bg-white/10 cursor-pointer",
      itemActive: "bg-white/10 text-white",
      divider: "border-t border-white/10 my-1",
    },

    // Avatars with glass effect
    avatar: {
      base: "inline-flex items-center justify-center rounded-full border border-white/10 bg-white/5 font-semibold",
      small: "h-8 w-8 text-xs",
      medium: "h-10 w-10 text-sm",
      large: "h-12 w-12 text-base",
    },

    divider: "border-t border-white/10",
    skeleton: "animate-pulse rounded-lg bg-white/5",

    // Spinner/Loader with glass effect
    spinner: "animate-spin rounded-full border-2 border-white/20 border-t-primary-500",
    spinnerXs: "h-3 w-3 animate-spin rounded-full border-2 border-white/20 border-t-primary-500",
    spinnerSmall: "h-4 w-4 animate-spin rounded-full border-2 border-white/20 border-t-primary-500",
    spinnerMedium: "h-5 w-5 animate-spin rounded-full border-2 border-white/20 border-t-primary-500",
    spinnerLarge: "h-6 w-6 animate-spin rounded-full border-2 border-white/20 border-t-primary-500",
    spinnerXl: "h-8 w-8 animate-spin rounded-full border-3 border-white/20 border-t-primary-500",

    // Legacy card support (use surfaces.card instead)
    card: "rounded-2xl border border-white/10 bg-neutral-900/60 backdrop-blur-md",
  },
} as const;

function isObject(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

function mergeTokens<T>(base: T, overrides?: DeepPartial<T>): T {
  if (!overrides) {
    return base;
  }

  const clone = Array.isArray(base)
    ? ([...base] as unknown as T)
    : ({
        ...(base as Record<string, unknown>),
      } as unknown as T);

  for (const key in overrides) {
    if (!Object.prototype.hasOwnProperty.call(overrides, key)) continue;
    const overrideValue = overrides[key];
    if (overrideValue === undefined) continue;
    const baseValue = (base as Record<string, unknown>)[key];

    if (isObject(baseValue) && isObject(overrideValue)) {
      (clone as Record<string, unknown>)[key] = mergeTokens(
        baseValue,
        overrideValue as DeepPartial<typeof baseValue>
      );
    } else {
      (clone as Record<string, unknown>)[key] = overrideValue as unknown;
    }
  }

  return clone;
}

function useApplyFontVariables(fonts: DesignTokens["typography"]["fonts"]) {
  useEffect(() => {
    if (typeof document === "undefined") return;
    const root = document.documentElement;
    Object.entries(FONT_VARIABLE_KEYS).forEach(([key, variable]) => {
      const fontValue = fonts[key as keyof typeof fonts];
      if (fontValue) {
        root.style.setProperty(variable, fontValue);
      }
    });
  }, [fonts]);
}

export const DesignSystemProvider = ({
  value,
  children,
}: PropsWithChildren<{ value?: DeepPartial<DesignTokens> }>) => {
  const tokens = useMemo(
    () => mergeTokens(defaultDesignTokens, value),
    [value]
  );

  useApplyFontVariables(tokens.typography.fonts);

  return (
    <DesignSystemContext.Provider value={tokens}>
      {children}
    </DesignSystemContext.Provider>
  );
};

export function useDesignSystem(): DesignTokens {
  const context = useContext(DesignSystemContext);
  if (!context) {
    return defaultDesignTokens;
  }
  return context;
}

export function createDesignTokens(
  overrides: DeepPartial<DesignTokens>
): DesignTokens {
  return mergeTokens(defaultDesignTokens, overrides);
}
